import { getPepHealthCheckDisplay, isPepPartialSetup } from './parsePepHealthCheckLastResult'
import {
  getEffectivePmsReportsInLast48Hours,
  isPmsSyncLastInfoStale
} from './pmsSyncDataRecency'

export const PEP_CSM_ACTION_KEYS = {
  none: 'none',
  installApp: 'installApp',
  partialSetup: 'partialSetup',
  checkPcOnline: 'checkPcOnline',
  dashboardLogin: 'dashboardLogin',
  pepLogin: 'pepLogin',
  escalateEngineering: 'escalateEngineering',
  hiltonAccess: 'hiltonAccess',
  pmsSyncIssue: 'pmsSyncIssue'
}

/** Lower rank = higher priority in default sort. */
export const PEP_CSM_ACTION_SORT_ORDER = {
  [PEP_CSM_ACTION_KEYS.escalateEngineering]: 0,
  [PEP_CSM_ACTION_KEYS.hiltonAccess]: 1,
  [PEP_CSM_ACTION_KEYS.installApp]: 2,
  [PEP_CSM_ACTION_KEYS.partialSetup]: 3,
  [PEP_CSM_ACTION_KEYS.checkPcOnline]: 4,
  [PEP_CSM_ACTION_KEYS.dashboardLogin]: 5,
  [PEP_CSM_ACTION_KEYS.pepLogin]: 6,
  [PEP_CSM_ACTION_KEYS.pmsSyncIssue]: 7,
  [PEP_CSM_ACTION_KEYS.none]: 8
}

const PEP_CSM_ACTION_DEFINITIONS = {
  [PEP_CSM_ACTION_KEYS.none]: {
    label: 'No action needed',
    instruction: 'No CSM action required — hotel is operating normally.',
    severity: 'success',
    reason: 'PEP health and sync look good.'
  },
  [PEP_CSM_ACTION_KEYS.installApp]: {
    label: 'Install app',
    instruction:
      'Send the PEP desktop installer and confirm a hotel user installs it and signs in to Directful.',
    severity: 'warn',
    reason: 'No desktop app telemetry has been received for this hotel.'
  },
  [PEP_CSM_ACTION_KEYS.partialSetup]: {
    label: 'Partial setup',
    instruction:
      'Some hotel users are set up, but others still do not have the PEP desktop app installed. Send the installer to every remaining user on the roster, have each install it on their PC, sign in to Directful, and complete Hilton PEP sign-in. Open View details to see which users show No app signal.',
    severity: 'warn',
    reason:
      'At least one hotel user has the app, but other dashboard users on the roster still need installation or sign-in.'
  },
  [PEP_CSM_ACTION_KEYS.checkPcOnline]: {
    label: 'Check PC is on',
    instruction:
      'Ask the hotel to turn the PC on, connect to the internet, and leave the Directful desktop app running. Do not reinstall.',
    severity: 'warn',
    reason: 'Desktop telemetry or PMS sync is stale — the PC may be off or the app closed.'
  },
  [PEP_CSM_ACTION_KEYS.dashboardLogin]: {
    label: 'Dashboard sign-in',
    instruction:
      'Ask a hotel user to open the Directful desktop app and sign in to the Directful dashboard.',
    severity: 'warn',
    reason: 'Onboarding is pending or dashboard sign-in is required.'
  },
  [PEP_CSM_ACTION_KEYS.pepLogin]: {
    label: 'Hilton PEP sign-in',
    instruction:
      'Ask a hotel user to open the desktop app and complete Hilton PEP sign-in when prompted.',
    severity: 'warn',
    reason: 'Hilton PEP sign-in is not verified on the latest device signals.'
  },
  [PEP_CSM_ACTION_KEYS.escalateEngineering]: {
    label: 'Escalate to engineering',
    instruction:
      'Escalate to engineering. PEP sign-in may be active but data is not flowing — check property configuration and re-login if advised.',
    severity: 'error',
    reason: 'Data is flowing partially but PEP authorization or data pull is failing.'
  },
  [PEP_CSM_ACTION_KEYS.hiltonAccess]: {
    label: 'Hilton access grant',
    instruction:
      'This is a Hilton-side access grant — re-login will not fix it. Ask Hilton to enable PEP reservation-search for this property.',
    severity: 'error',
    reason: 'Health Check reports a PEP authorization problem with onboarding complete.'
  },
  [PEP_CSM_ACTION_KEYS.pmsSyncIssue]: {
    label: 'PMS sync issue',
    instruction:
      'Review PMS sync status and credentials. Use Test login or contact support if sync remains broken.',
    severity: 'warn',
    reason: 'PMS sync is disconnected, stale, or reporting problems.'
  }
}

/** Action keys shown in the Health Check PEP CSM action column (and filter dropdown). */
export const PEP_CSM_ACTION_COLUMN_KEYS = [
  PEP_CSM_ACTION_KEYS.escalateEngineering,
  PEP_CSM_ACTION_KEYS.hiltonAccess,
  PEP_CSM_ACTION_KEYS.installApp,
  PEP_CSM_ACTION_KEYS.partialSetup,
  PEP_CSM_ACTION_KEYS.checkPcOnline,
  PEP_CSM_ACTION_KEYS.dashboardLogin,
  PEP_CSM_ACTION_KEYS.pepLogin,
  PEP_CSM_ACTION_KEYS.none
]

/** Filter dropdown options — same labels as the CSM action column. */
export const PEP_CSM_ACTION_FILTER_OPTIONS = PEP_CSM_ACTION_COLUMN_KEYS.reduce(
  (options, actionKey) => {
    options[actionKey] = PEP_CSM_ACTION_DEFINITIONS[actionKey].label
    return options
  },
  {}
)

export function getPepCsmActionFilterLabel(actionKey) {
  return (
    PEP_CSM_ACTION_FILTER_OPTIONS[actionKey] ||
    PEP_CSM_ACTION_DEFINITIONS[actionKey]?.label ||
    actionKey
  )
}

/** Drops filter values that never appear in the PEP CSM action column. */
export function sanitizePepCsmActionFilterKeys(selectedActionKeys) {
  if (!Array.isArray(selectedActionKeys)) {
    return []
  }

  const allowedKeys = new Set(PEP_CSM_ACTION_COLUMN_KEYS)
  return selectedActionKeys.filter(key => allowedKeys.has(key))
}

function buildAction(actionKey, reasonOverride) {
  const definition = PEP_CSM_ACTION_DEFINITIONS[actionKey] || PEP_CSM_ACTION_DEFINITIONS.none
  return {
    actionKey,
    actionLabel: definition.label,
    actionInstruction: definition.instruction,
    severity: definition.severity,
    reason: reasonOverride || definition.reason
  }
}

/**
 * Derives a single CSM-facing action for a Health Check PMS row.
 * Uses list-level fields only (no telemetry API).
 *
 * @param {object} row
 * @returns {{ actionKey: string, actionLabel: string, actionInstruction: string, severity: string, reason: string }}
 */
export function derivePepCsmAction(row) {
  const {
    pms,
    lastResult,
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours,
    isHavingProblems,
    pmsSyncIsConnected,
    isDisabled
  } = row || {}

  if (pms !== 'pep') {
    const syncStale = isPmsSyncLastInfoStale(pmsSyncLastInfoReceivedOn)
    if (
      pmsSyncIsConnected === false ||
      isHavingProblems === true ||
      (!isDisabled && syncStale)
    ) {
      return buildAction(PEP_CSM_ACTION_KEYS.pmsSyncIssue)
    }
    return buildAction(PEP_CSM_ACTION_KEYS.none)
  }

  const reportsInLast48Hours = getEffectivePmsReportsInLast48Hours(
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours
  )
  const syncStale = isPmsSyncLastInfoStale(pmsSyncLastInfoReceivedOn)
  const pepDisplay = getPepHealthCheckDisplay(lastResult, pms, reportsInLast48Hours)

  if (!pepDisplay) {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  const {
    badgeKey,
    issueDevices,
    activeDevices,
    onboardingState,
    futureState,
    isNoTelemetryFallback
  } = pepDisplay

  if (badgeKey === 'AppNotInstalled') {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  if (isNoTelemetryFallback && reportsInLast48Hours === 0 && activeDevices === 0) {
    return buildAction(PEP_CSM_ACTION_KEYS.installApp)
  }

  if (badgeKey === 'PepAuthorizationError') {
    if (reportsInLast48Hours > 0 && issueDevices > 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.escalateEngineering)
    }
    if (onboardingState === 'Done' && futureState === 'Active') {
      return buildAction(PEP_CSM_ACTION_KEYS.hiltonAccess)
    }
    return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
  }

  if (syncStale) {
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (isPepPartialSetup(pepDisplay, row)) {
    return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
  }

  if (badgeKey === 'NeedsAttention' && activeDevices === 0) {
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (badgeKey === 'NeedsAttention') {
    if (onboardingState === 'Pending') {
      return buildAction(PEP_CSM_ACTION_KEYS.dashboardLogin)
    }
    if (issueDevices > 0 && reportsInLast48Hours === 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
    }
    if (futureState === 'Inactive') {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
    if (issueDevices > 0) {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
  }

  if (badgeKey === 'Working') {
    if (issueDevices === 0) {
      if (isPepPartialSetup(pepDisplay, row)) {
        return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
      }
      return buildAction(PEP_CSM_ACTION_KEYS.none)
    }
    if (onboardingState === 'Pending') {
      return buildAction(PEP_CSM_ACTION_KEYS.dashboardLogin)
    }
    if (futureState === 'Inactive') {
      return buildAction(PEP_CSM_ACTION_KEYS.pepLogin)
    }
    return buildAction(PEP_CSM_ACTION_KEYS.checkPcOnline)
  }

  if (isPepPartialSetup(pepDisplay, row)) {
    return buildAction(PEP_CSM_ACTION_KEYS.partialSetup)
  }

  return buildAction(PEP_CSM_ACTION_KEYS.none)
}

export function resolvePepCsmActionSortRank(row) {
  const action = derivePepCsmAction(row)
  return PEP_CSM_ACTION_SORT_ORDER[action.actionKey] ?? Number.MAX_SAFE_INTEGER
}

/**
 * @param {string} actionKey
 * @param {string[]} selectedActionKeys - empty means no filter
 */
export function matchesPepCsmActionFilter(actionKey, selectedActionKeys) {
  if (!selectedActionKeys || selectedActionKeys.length === 0) {
    return true
  }
  return selectedActionKeys.includes(actionKey)
}

/**
 * Counts CSM actions for the current filtered hotel list.
 * @param {object[]} hotels
 */
export function buildPepCsmActionSummary(hotels) {
  const counts = Object.values(PEP_CSM_ACTION_KEYS).reduce((acc, key) => {
    acc[key] = 0
    return acc
  }, {})

  for (const hotel of hotels || []) {
    const { actionKey } = derivePepCsmAction(hotel)
    counts[actionKey] = (counts[actionKey] || 0) + 1
  }

  const total = (hotels || []).length
  const needingAction = total - (counts[PEP_CSM_ACTION_KEYS.none] || 0)

  return { counts, total, needingAction }
}

/** Queue chips shown in the summary bar (excludes none and pmsSyncIssue). */
export const PEP_CSM_ACTION_SUMMARY_KEYS = [
  PEP_CSM_ACTION_KEYS.installApp,
  PEP_CSM_ACTION_KEYS.partialSetup,
  PEP_CSM_ACTION_KEYS.checkPcOnline,
  PEP_CSM_ACTION_KEYS.dashboardLogin,
  PEP_CSM_ACTION_KEYS.pepLogin,
  PEP_CSM_ACTION_KEYS.escalateEngineering,
  PEP_CSM_ACTION_KEYS.hiltonAccess
]
